import { Component, Input, Output, EventEmitter, inject, HostBinding, ElementRef, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ButtonVariant, ButtonSize } from '../../../types/ui.types';
import { cn } from '../../../utils/class-utils';
import { ResponsiveService } from '../../../services/responsive.service';
import { map } from 'rxjs/operators';

@Component({
  selector: 'jjames-button',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  template: `
    <div class="relative inline-block" (mouseenter)="onMouseEnter()" (mouseleave)="onMouseLeave()">
      <button
        [class]="buttonClasses"
        [disabled]="disabled || loading"
        [type]="type"
        (click)="handleClick($event)"
      >
      <!-- Loading spinner -->
      @if (loading) {
        <svg
          class="animate-spin -ml-1 mr-3 h-5 w-5 text-current"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      }

      <!-- Custom SVG icon -->
      @if (svgIcon && !loading) {
        <svg [class]="iconClasses" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="svgIcon" />
        </svg>
      }

      <!-- Material Icon before content -->
      @if (matIconLeft && !loading && !svgIcon) {
        <mat-icon [class]="iconClasses">{{ matIconLeft }}</mat-icon>
      }

      <!-- Icon before content -->
      @if (iconLeft && !loading && !svgIcon && !matIconLeft) {
        <i [class]="iconLeft + ' ' + iconClasses"></i>
      }

      <!-- Button content -->
      @if (showText) {
        <span [class]="loading ? 'opacity-75' : '' + ' btn-text'">
          <ng-content></ng-content>
        </span>
      }

      <!-- Icon after content -->
      @if (iconRight && !svgIcon && !matIconRight) {
        <i [class]="iconRight + ' ' + iconClasses"></i>
      }

      <!-- Material Icon after content -->
      @if (matIconRight && !svgIcon) {
        <mat-icon [class]="iconClasses">{{ matIconRight }}</mat-icon>
      }
      </button>
      
      <!-- Tooltip -->
      @if (shouldShowTooltip && showTooltip && !disabled && !loading) {
        <div class="tooltip-container">
          <div class="tooltip-content">
            {{ getTooltipText() }}
            <div class="tooltip-arrow"></div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`    
    .tooltip-container {
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      margin-bottom: 8px;
      z-index: 1000;
      pointer-events: none;
      width: max-content;
    }
    
    .tooltip-content {
      background: rgba(17, 24, 39, 0.9);
      color: white;
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 0.75rem;
      font-weight: 500;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      backdrop-filter: blur(8px);
      position: relative;
      animation: tooltipFadeIn 0.2s ease-out;
      margin: 0 auto;
      text-align: center;
    }
    
    .tooltip-arrow {
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid rgba(17, 24, 39, 0.9);
    }
    
    @keyframes tooltipFadeIn {
      from {
        opacity: 0;
        transform: translateY(4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    /* Responsive adjustments */
    @media (max-width: 640px) {
      .tooltip-content {
        font-size: 0.7rem;
        padding: 4px 8px;
      }
    }

    /* Material icon adjustments */
    mat-icon {
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      vertical-align: middle;
    }
  `]
})
export class JjamesButtonComponent {
  @Input() variant: ButtonVariant = 'primary';
  @Input() size: ButtonSize = 'md';
  @Input() disabled = false;
  @Input() loading = false;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() fullWidth = false;
  @Input() iconLeft?: string;
  @Input() iconRight?: string;
  @Input() matIconLeft?: string; // Material icon before content
  @Input() matIconRight?: string; // Material icon after content
  @Input() svgIcon?: string; // SVG path for custom icons
  @Input() tooltip?: string; // Custom tooltip text
  @Input() showText = true;
  @Output() clicked = new EventEmitter<Event>();

  // Tooltip state
  showTooltip = false;
  private buttonText = '';
  private elementRef = inject(ElementRef);

  private responsiveService = inject(ResponsiveService);

  // Track handset state
  isHandset$ = this.responsiveService.isHandset$;

  @HostBinding('class') get hostClass() {
    // We can't use async pipe in host binding, so we'll handle this differently
    return '';
  }

  get buttonClasses(): string {
    const baseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variantClasses = this.getVariantClasses();
    const sizeClasses = this.getSizeClasses();
    const widthClasses = this.fullWidth ? 'w-full' : '';
    const actionClasses = this.isActionButton() ? 'action-btn' : '';

    return cn(baseClasses, variantClasses, sizeClasses, widthClasses, actionClasses);
  }

  get iconClasses(): string {
    const baseClasses = 'flex-shrink-0';
    const sizeClasses = this.getIconSizeClasses();
    const spacingClasses = this.getIconSpacingClasses();

    return cn(baseClasses, sizeClasses, spacingClasses);
  }

  private isActionButton(): boolean {
    return this.variant.startsWith('action-');
  }

  private getVariantClasses(): string {
    const variants: Record<ButtonVariant, string> = {
      primary: 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500 rounded-md',
      secondary: 'bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-500 rounded-md',
      success: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500 rounded-md',
      danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 rounded-md',
      warning: 'bg-yellow-600 hover:bg-yellow-700 text-white focus:ring-yellow-500 rounded-md',
      info: 'bg-cyan-600 hover:bg-cyan-700 text-white focus:ring-cyan-500 rounded-md',
      outline: 'border border-gray-300 bg-transparent hover:bg-gray-50 text-gray-700 focus:ring-gray-500 rounded-md',
      ghost: 'bg-transparent hover:bg-gray-100 text-gray-700 focus:ring-gray-500 rounded-md',
      'action-edit': 'text-amber-600 bg-amber-50 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 focus:ring-amber-500 rounded-md hover:transform hover:-translate-y-1 hover:shadow-md active:scale-95',
      'action-delete': 'text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 hover:border-red-300 focus:ring-red-500 rounded-md hover:transform hover:-translate-y-1 hover:shadow-md active:scale-95',
      'action-unassign': 'text-purple-600 bg-purple-50 border border-purple-200 hover:bg-purple-100 hover:border-purple-300 focus:ring-purple-500 rounded-md hover:transform hover:-translate-y-1 hover:shadow-md active:scale-95',
      'action-details': 'text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 focus:ring-blue-500 rounded-md hover:transform hover:-translate-y-1 hover:shadow-md active:scale-95'
    };
    return variants[this.variant];
  }

  private getSizeClasses(): string {
    if (this.isActionButton()) {
      const actionSizes: Record<ButtonSize, string> = {
        xs: 'px-2 py-1.5 text-xs h-7',
        sm: 'px-2.5 py-1.5 text-xs h-8',
        md: 'px-3 py-2 text-xs h-8',
        lg: 'px-4 py-2 text-sm h-9',
        xl: 'px-6 py-2.5 text-sm h-10'
      };
      return actionSizes[this.size];
    }

    const sizes: Record<ButtonSize, string> = {
      xs: 'px-2.5 py-1.5 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-4 py-2 text-base',
      xl: 'px-6 py-3 text-base'
    };
    return sizes[this.size];
  }

  private getIconSizeClasses(): string {
    if (this.isActionButton()) {
      return 'w-4 h-4';
    }

    const sizes: Record<ButtonSize, string> = {
      xs: 'w-3 h-3',
      sm: 'w-4 h-4',
      md: 'w-4 h-4',
      lg: 'w-5 h-5',
      xl: 'w-5 h-5'
    };
    return sizes[this.size];
  }

  private getIconSpacingClasses(): string {
    if (this.isActionButton()) {
      return 'mr-1';
    }
    // For left icons (Font Awesome or Material), add right margin if there's text
    // For right icons, no margin needed as they come after the text
    return this.showText ? 'mr-2' : '';
  }

  handleClick(event: Event): void {
    if (!this.disabled && !this.loading) {
      this.clicked.emit(event);
    }
  }

  onMouseEnter(): void {
    this.showTooltip = true;
    this.updateButtonText();
  }

  onMouseLeave(): void {
    this.showTooltip = false;
  }

  get shouldShowTooltip(): boolean {
    return !!(this.tooltip || this.shouldShowButtonTextAsTooltip());
  }

  private shouldShowButtonTextAsTooltip(): boolean {
    // Show button text as tooltip on handset for action buttons when text is hidden
    let isHandset = false;
    this.isHandset$.subscribe(handset => isHandset = handset).unsubscribe();
    return isHandset && this.isActionButton() && !!this.buttonText;
  }

  getTooltipText(): string {
    if (this.tooltip) {
      return this.tooltip;
    }

    // For handset, show button text as tooltip for action buttons
    let isHandset = false;
    this.isHandset$.subscribe(handset => isHandset = handset).unsubscribe();

    if (isHandset && this.isActionButton() && this.buttonText) {
      return this.buttonText;
    }

    return '';
  }

  private updateButtonText(): void {
    // Extract text content from the button
    const buttonElement = this.elementRef.nativeElement.querySelector('button');
    if (buttonElement) {
      const textSpan = buttonElement.querySelector('.btn-text');
      if (textSpan) {
        this.buttonText = textSpan.textContent?.trim() || '';
      }
    }
  }
}